import { createContext, type ReactNode, useEffect, useMemo } from 'react';
import { getCookie, setCookie } from 'cookies-next';
import { useGetIso } from '@/api';
import { useUser } from '@/lib/auth/useUser';

/**
 * 사용자의 국가 코드 및 관련 정보를 관리하는 Context
 *
 * @description
 * - 로그인 사용자: user 객체의 countryCode 사용
 * - 비로그인 사용자: 'countryCode' 쿠키 사용
 *
 **/

interface CountryCodeContextValue {
  countryCode: string;
  /**
   * 실제 출처(로그인 유저 / 쿠키 / ISO 조회)에서 확인된 국가코드.
   * 어느 출처도 없어 countryCode 가 기본값('US')으로 떨어진 경우 undefined.
   * "국가를 실제로 안다"는 확신이 필요한 소비자(예: CRM 국가 타겟팅)는 이 값만
   * 신뢰한다 — 미확인 유저에게 가짜 국가를 부여해 오노출되지 않도록.
   */
  resolvedCountryCode: string | undefined;
  isLoading: boolean;
  isKR: boolean;
  isReady: boolean;
}
export const CountryCodeContext = createContext<CountryCodeContextValue | undefined>(undefined);
export function CountryCodeProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    isAuthenticated,
    user,
    isInitialized
  } = useUser();

  // 쿠키에서 국가코드 가져오기
  const countryCodeCookie = getCookie('countryCode');

  // 쿠키 없으면 API 호출
  const {
    data: isoData,
    isLoading: isIsoLoading
  } = useGetIso({
    query: {
      enabled: isInitialized && !countryCodeCookie && !user?.countryCode
    }
  });

  // 위 API로 받아온 국가코드를 쿠키에 저장
  useEffect(() => {
    if (!countryCodeCookie && isoData?.data.isoCode) {
      setCookie('countryCode', isoData.data.isoCode, {
        maxAge: 60 * 60 * 24 * 7
      });
    }
  }, [countryCodeCookie, isoData?.data.isoCode]);

  // 실제 출처에서 확인된 국가(우선순위: 로그인 유저 > 쿠키 > ISO API).
  // 어느 출처도 없으면 undefined — 기본값 'US' 로 위조하지 않는다.
  const resolvedSource = isAuthenticated && user?.countryCode || countryCodeCookie || isoData?.data.isoCode || '';
  const resolvedCountryCode = typeof resolvedSource === 'string' && resolvedSource ? resolvedSource.toUpperCase() : undefined;

  // 최종 국가코드 결정 (기존 소비자 호환: 미확인 시 'US' 기본값 유지)
  const countryCode = resolvedCountryCode ?? 'US';
  const isLoading = !isInitialized || isIsoLoading;
  const value = useMemo(() => ({
    countryCode,
    resolvedCountryCode,
    isKR: countryCode === 'KR',
    isLoading,
    isReady: !isLoading && !!countryCode
  }), [countryCode, resolvedCountryCode, isLoading]);
  return <CountryCodeContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="CountryCodeProvider" data-sentry-source-file="CountryCodeProvider.tsx">
      {children}
    </CountryCodeContext.Provider>;
}